import { useState, useEffect } from 'react';
import { useSuperQuery } from '@vadiun/react-hooks-legacy';
import useViajesTraslado from '../services/ViajeTrasladoService';
import { TransferReservationBaseFiltersFormType } from '../components/ViajeTrasladoBaseFilters';
import {
  CoordinateColumnVisibility,
  getDefaultFilters,
  getDefaultColumnVisibility,
  getDefaultSort,
  loadStateFromLocalStorage,
  saveStateToLocalStorage
} from '../utils/coordinateStorage';

// Re-exportar para mantener compatibilidad
export { columnsLabels, getDefaultFilters } from '../utils/coordinateStorage';

export const useCoordinateReserves = () => {
  const coordinateService = useViajesTraslado();

  // Un solo estado que engloba todo
  const [state, setState] = useState(loadStateFromLocalStorage);

  // Sincronizar con localStorage
  useEffect(() => {
    saveStateToLocalStorage(state);
  }, [state]);

  // Usar valores primitivos para las dependencias de useSuperQuery
  const getCoordinateReserves = useSuperQuery(
    () =>
      coordinateService.getPendingAssigment(
        state.page,
        state.rowsPerPage,
        state.sort.sortBy,
        state.sort.sortDir,
        state.filters
      ),
    { showSpinner: true },
    [state.page, state.rowsPerPage, state.sort.sortBy, state.sort.sortDir, JSON.stringify(state.filters)]
  );

  const onChangePage = (newPage: number) => {
    if (newPage !== state.page) {
      setState(prev => ({ ...prev, page: newPage }));
    }
  };

  const onFiltersChange = (filtersEntry: TransferReservationBaseFiltersFormType) => {
    setState(prev => ({ ...prev, filters: filtersEntry, page: 1 }));
  };

  const onSearchChange = (search: string) => {
    setState(prev => ({ ...prev, filters: { ...prev.filters, search }, page: 1 }));
  };

  const onChangeRowsPerPage = (newRowsPerPage: number) => {
    setState(prev => ({ ...prev, rowsPerPage: newRowsPerPage, page: 1 }));
  };

  const onSortChange = (direction: string | null, sortBy: string) => {
    setState(prev => ({
      ...prev,
      sort: {
        sortDir: direction === 'desc' ? 'DESC' : 'ASC',
        sortBy
      },
      page: 1
    }));
  };

  const setColumnVisibility = (newColumnVisibility: CoordinateColumnVisibility) => {
    setState(prev => ({ ...prev, columnVisibility: newColumnVisibility }));
  };

  const resetState = () => {
    setState({
      page: 1,
      rowsPerPage: 100,
      sort: getDefaultSort(),
      filters: getDefaultFilters(),
      columnVisibility: getDefaultColumnVisibility()
    });
  };

  return {
    coordinateReserves: {
      data: getCoordinateReserves.data?.data || [],
      total: getCoordinateReserves.data?.total || 0,
      isLoading: getCoordinateReserves.isLoading,
      error: getCoordinateReserves.error,
      serverSideOptions: {
        page: state.page - 1, // Convert to 0-based for MUI DataTable
        count: getCoordinateReserves.data?.total || 0,
        rowsPerPage: state.rowsPerPage,
        sortOrder: {
          name: state.sort.sortBy,
          direction: state.sort.sortDir.toLowerCase() as 'asc' | 'desc'
        }
      }
    },
    reload: getCoordinateReserves.reload,
    filters: state.filters,
    page: state.page,
    rowsPerPage: state.rowsPerPage,
    columnVisibility: state.columnVisibility,
    setColumnVisibility,
    resetState,
    onChangePage,
    onChangeRowsPerPage,
    onSortChange,
    onFiltersChange,
    onSearchChange
  };
};
